import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { problemsApi } from '@/api/endpoints';
import type { ApiProblemTopic } from '@/api/types';
import { TOPICS, topicPath } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { pageSeoTitle } from '@/lib/seoText';
import { TOPICS_INDEX_PATH, topicsIndexStructuredData } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';
import { CollectionLinks } from '@/components/problems/ProblemCollection';

type CountMap = Partial<Record<ApiProblemTopic, number>>;

const toCountMap = (rows: { topic: ApiProblemTopic; count: number }[]): CountMap =>
  Object.fromEntries(rows.map((r) => [r.topic, r.count]));

/** Mavzular indeksi: /topics — barcha mavzu sahifalariga hub (breadcrumb'dagi "Mavzular") */
export function Topics() {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const initial = useInitialData();
  const [counts, setCounts] = useState<CountMap | null>(() =>
    initial?.topicCounts ? toCountMap(initial.topicCounts) : null,
  );

  useEffect(() => {
    let cancelled = false;
    problemsApi
      .topics()
      .then((res) => {
        if (!cancelled) setCounts(toCountMap(res.data));
      })
      .catch(() => {
        if (!cancelled) setCounts((prev) => prev ?? {});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useSeo({
    title: pageSeoTitle('SQL mavzulari — masalalar mavzu bo\'yicha'),
    description:
      "SQL masalalari mavzu bo'yicha: SELECT, JOIN, agregatsiya, subquery, window funksiyalar, CTE va indekslar. Mavzuni tanlang va real jadvallar ustida mashq qiling.",
    path: TOPICS_INDEX_PATH,
    jsonLd: topicsIndexStructuredData(),
  });

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {t('nav.topics')}
          </li>
        </ol>
      </nav>

      <div className="mb-8">
        <h1 className="text-2xl font-bold sm:text-3xl">{t('topicsIndex.title')}</h1>
        <p className="mt-2 max-w-3xl text-content-secondary">{t('topicsIndex.subtitle')}</p>
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {TOPICS.map((topic) => (
          <li key={topic.slug}>
            <Link to={topicPath(topic.slug)} className="card card-hover group flex h-full flex-col p-5">
              <div className="mb-2 flex items-center gap-2">
                <topic.icon className="h-5 w-5 shrink-0 text-brand-600" aria-hidden />
                <h2 className="text-lg font-semibold transition-colors group-hover:text-brand-600">
                  {l(topic.title)}
                </h2>
              </div>
              <p className="flex-1 text-sm text-content-secondary">{l(topic.desc)}</p>
              <p className="mt-3 text-xs text-content-muted">
                {counts === null ? (
                  <span className="inline-block h-3 w-12 animate-pulse rounded bg-surface" />
                ) : (
                  t('common.problemsCount', { count: counts[topic.slug] ?? 0 })
                )}
              </p>
            </Link>
          </li>
        ))}
      </ul>

      <CollectionLinks current={TOPICS_INDEX_PATH} />
    </div>
  );
}
